import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Clock, DollarSign, Users, Target, TrendingUp, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';
import { pageConfigs } from '../utils/seo';

const PTSalesRepApplication: React.FC = () => {
  return (
    <>
      <MetaTags {...pageConfigs.careers} />

      <div className="min-h-screen bg-gradient-to-br from-[#0a1a1a] via-[#1a2f2f] to-[#2d4a4a]">
        {/* Header */}
        <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <Link
                to="/careers"
                className="inline-flex items-center gap-2 text-[#58ACDD] hover:text-white transition-colors duration-300"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back to Careers</span>
              </Link>
            </motion.div>

            {/* Job Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-productSans">
                PT / PTA – Clinic Outreach & Sales Rep
              </h1>

              <div className="flex flex-wrap gap-6 text-white/70 mb-6">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">Remote (Anywhere in the US)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Contract, Flexible</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">$1,000/month base + $100–$150/clinic commission</span>
                </div>
              </div>

              <div className="inline-block bg-[#58ACDD]/20 text-[#58ACDD] px-4 py-2 rounded-full text-sm font-medium">
                SALES • CONTRACT • REMOTE
              </div>
            </motion.div>
          </div>
        </section>

        {/* Job Details */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-12">
                
                {/* About Notation */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
                >
                  <h2 className="text-2xl font-bold text-white mb-6 font-productSans flex items-center gap-3">
                    <Heart className="w-6 h-6 text-[#58ACDD]" />
                    About Notation
                  </h2>
                  <div className="space-y-4 text-white/80">
                    <p>
                      We're FOWND Inc., the team behind Notation — an AI-powered documentation assistant built by PTs, for PTs.
                    </p>
                    <p>
                      Our goal is simple: give therapists their time back.
                    </p>
                    <p>
                      Notation runs right next to any EMR, writes in real time, and keeps PTs in full control of every note.
                    </p>
                  </div>
                </motion.div>

                {/* The Role */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
                >
                  <h2 className="text-2xl font-bold text-white mb-6 font-productSans flex items-center gap-3">
                    <Users className="w-6 h-6 text-[#58ACDD]" />
                    The Role
                  </h2>
                  <div className="space-y-4 text-white/80">
                    <p>
                      We're looking for a PT or PTA who enjoys connecting with people and building relationships.
                    </p>
                    <p>
                      You'll reach out to outpatient clinics, introduce Notation, and help owners see how it can make their workday smoother.
                    </p>
                    <p>
                      Our team will help you run initial demos and close deals — you focus on the first contact and relationship-building.
                    </p>
                    <p className="font-medium text-[#58ACDD]">
                      This is a contract, results-driven position — we don't track hours, only outcomes.
                    </p>
                  </div>
                </motion.div>

                {/* What You'll Do */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
                >
                  <h2 className="text-2xl font-bold text-white mb-6 font-productSans flex items-center gap-3">
                    <Target className="w-6 h-6 text-[#58ACDD]" />
                    What You'll Do & How Success Is Measured
                  </h2>
                  <div className="space-y-4 text-white/80">
                    <div className="space-y-3">
                      <p><strong className="text-white">Required:</strong> Make 8–10 outbound calls per day (40–50 per week) to introduce Notation.</p>
                      <p>Follow up with interested clinic owners or directors to schedule demos.</p>
                      <p>Keep quick notes and status updates in our CRM.</p>
                      <p>Visit nearby clinics to strengthen relationships.</p>
                      <p>Share feedback from conversations to improve outreach strategy.</p>
                    </div>
                    <div className="bg-[#58ACDD]/10 border border-[#58ACDD]/20 rounded-lg p-4 mt-6">
                      <p className="text-[#58ACDD] font-medium">Success Target:</p>
                      <p className="text-white/90">Aim for 4–6 demos to be scheduled and 1–2 new clinics to be signed each month after the ramp-up period.</p>
                    </div>
                    <p className="text-sm text-white/60">
                      *Additional incentive for group virtual demos/lunch and learn sessions, and content creation that FOWND can market in our marketing campaigns.
                    </p>
                  </div>
                </motion.div>

                {/* Compensation */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
                >
                  <h2 className="text-2xl font-bold text-white mb-6 font-productSans flex items-center gap-3">
                    <TrendingUp className="w-6 h-6 text-[#58ACDD]" />
                    Compensation
                  </h2>
                  <div className="space-y-4 text-white/80">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-[#58ACDD]/10 border border-[#58ACDD]/20 rounded-lg p-4">
                        <h4 className="font-semibold text-white mb-2">Base Stipend</h4>
                        <p className="text-2xl font-bold text-[#58ACDD]">$1,000</p>
                        <p className="text-sm">monthly base stipend</p>
                      </div>
                      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                        <h4 className="font-semibold text-white mb-2">Commission</h4>
                        <p className="text-2xl font-bold text-green-400">$100–$150</p>
                        <p className="text-sm">per clinic that signs up</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mt-6">
                      <p><strong className="text-white">Commission Structure:</strong></p>
                      <ul className="space-y-2 ml-4">
                        <li>• $100 per clinic for the first 25</li>
                        <li>• $125 for clinics 25-50</li>
                        <li>• $150 for any above 50 in a quarter</li>
                      </ul>
                      
                      <p className="mt-4"><strong className="text-white">Additional Benefits:</strong></p>
                      <ul className="space-y-2 ml-4">
                        <li>• $10/clinic/year residual commissions for retained clinics</li>
                        <li>• $250 per group demo with 5+ registered trial follow-ups</li>
                        <li>• $1,000 milestone bonus for 50 clinics</li>
                        <li>• $500 bonus for exceeding quota by 10%</li>
                      </ul>
                      
                      <p className="mt-4 text-[#58ACDD]">
                        Flexible, remote work structure — manage your own schedule.
                      </p>
                      <p className="text-sm text-white/60">
                        Contract position with an initial term expected to last several months, with the potential for renewal or expansion based on performance and company growth.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Who You Are */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
                >
                  <h2 className="text-2xl font-bold text-white mb-6 font-productSans">
                    Who You Are
                  </h2>
                  <div className="space-y-4 text-white/80">
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                        <span>PT, PTA, or a former clinician who speaks the language of outpatient rehab.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                        <span>Friendly, confident communicator who enjoys outreach and relationship-building.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                        <span>Organized, self-starter who follows through.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                        <span>A social media following and active participation within PT, OT, PTA, and COTA groups are a plus.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                        <span>Willingness to create content for Marketing and Sales Campaigns is desired.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-[#58ACDD] rounded-full mt-2 flex-shrink-0"></div>
                        <span>Curious about how AI can help clinicians and improve care.</span>
                      </li>
                    </ul>
                  </div>
                </motion.div>

                {/* Why It Matters */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
                >
                  <h2 className="text-2xl font-bold text-white mb-6 font-productSans">
                    Why It Matters
                  </h2>
                  <div className="space-y-4 text-white/80">
                    <p>
                      You'll help clinics discover a tool that lets PTs finish notes in-session — not after hours.
                    </p>
                    <p className="text-[#58ACDD] font-medium">
                      It's flexible, purposeful work that supports our profession and rewards initiative.
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 sticky top-8"
                >
                  <h3 className="text-xl font-bold text-white mb-6 font-productSans">
                    Ready to Apply?
                  </h3>
                  
                  <div className="space-y-6">
                    <div className="text-white/80 text-sm">
                      <p className="mb-4">
                        Join us in transforming healthcare documentation and help PTs reclaim their time.
                      </p>
                      <p>
                        We're looking for passionate healthcare professionals who want to make a real impact.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="w-full bg-gray-500 text-white px-6 py-4 rounded-full font-semibold flex items-center justify-center gap-2 opacity-60 cursor-not-allowed">
                        CLOSED
                      </div>
                      
                      <p className="text-white/60 text-xs text-center">
                        This position is no longer accepting applications
                      </p>
                    </div>

                    <div className="border-t border-white/10 pt-6">
                      <h4 className="text-white font-medium mb-3">Interview Process</h4>
                      <div className="space-y-3 text-sm text-white/70">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-[#58ACDD]/20 rounded-full flex items-center justify-center text-xs font-bold text-[#58ACDD]">1</div>
                          <span>Application Review</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-[#58ACDD]/20 rounded-full flex items-center justify-center text-xs font-bold text-[#58ACDD]">2</div>
                          <span>Phone/Video Interview</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-[#58ACDD]/20 rounded-full flex items-center justify-center text-xs font-bold text-[#58ACDD]">3</div>
                          <span>Team Meet & Greet</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-[#58ACDD]/20 rounded-full flex items-center justify-center text-xs font-bold text-[#58ACDD]">4</div>
                          <span>Onboarding & Start</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PTSalesRepApplication;
